import { Routes, Route } from "react-router-dom";
import { CrudList } from "./CrudList";
import { CrudLayout } from "./CrudLayout";
import { CrudCreateDialog } from "./CrudCreateDialog";
import { CrudEditDialog } from "./CrudEditDialog";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import type { Schema } from "../Form/types";
import type { DocumentNode } from "@apollo/client";
import type { OperationDefinitionNode } from "graphql";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import type {
  ColumnDefinition,
  // FilterData,
  CrudTexts,
  MutationVariables,
  TableData,
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "./types";
import type { CrudTotalSchemaItem } from "./CrudTotal";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const defaultTexts = (t: TFunction, texts: CrudTexts = {}): CrudTexts => ({
  filterTitle: "Filter",
  listTitle: "Items list",
  itemCreated: "Item created",
  itemUpdated: "Item updated",
  createItem: "Create item",
  refetchList: "Refetch list",
  deleteItems: "Delete items",
  itemsDeleted: "Items deleted",
  itemsRepeated: "Items repeated",
  itemsRestored: "Items restored",
  repeatItems: "Repeat items",
  cancel: "Cancel",
  editItem: "Edit item",
  showTitle: "Details",
  unsavedChangesWarning: t("shared.form.unsavedChangesWarning", {
    defaultValue:
      "У вас есть несохраненные изменения. Покинуть страницу без сохранения?",
  }),
  ...texts,
});

export interface CRUDProps<
  Row extends { id: string } = { id: string },
  Filter = Record<string, unknown>,
> {
  children?: React.ReactNode;
  totalSchema?: CrudTotalSchemaItem[];
  filterSchema?: Schema;
  filterQuery?: DocumentNode; // GraphQL query
  filterQueryVariables?: MutationVariables;
  autoFilter?: boolean;
  listQuery: DocumentNode; // GraphQL query
  listPollInterval?: number;
  listQueryVariables?: MutationVariables;
  listMetrics?: React.ComponentType<{ filter: Filter }>;
  texts?: CrudTexts;
  getTableData: (data: unknown) => TableData<Row>;
  mapColumns: (row: Row) => any[];
  columns: ColumnDefinition<Row>[];
  download?: boolean;
  downloadReportType?: string;
  downloadButton?: React.ReactNode;
  /** Нормализация значений фильтра перед отправкой в listQuery */
  normalizeFilter?: (filter: Record<string, any>) => Record<string, any>;
  createSchema?: CreateSchemaDefinition;
  deleteMutation?: DocumentNode; // GraphQL mutation
  deleteMutationVariables?: MutationVariables;
  repeatMutation?: DocumentNode; // GraphQL mutation
  repeatMutationVariables?: MutationVariables;
  repeatFromHlsMutation?: DocumentNode; // GraphQL mutation
  restoreMutation?: DocumentNode; // GraphQL mutation
  editSchema?: EditSchemaDefinition;
  editQuery?: DocumentNode; // GraphQL query
  /** Кнопки в тулбаре списка; функция получает id выбранных строк */
  headButtons?: React.ReactNode | ((selectedIds: string[]) => React.ReactNode);
  createInitialVariables?: Partial<Row>;
  onSubmitEditDialog?: (data: Row) => void;
  /** Сохранять значения фильтра в localStorage и восстанавливать при возврате */
  persistFilters?: boolean;
  /** Показывать список карточками вместо таблицы (нужны mobileColumns) */
  useMobileView?: boolean;
  mobileColumns?: DataListViewColumns<Row>;
  /** Какие строки можно выбирать; у остальных чекбокс скрыт */
  hasShowSelect?: (row: Row) => boolean;
  /** Показывать чекбоксы, даже если нет мутаций удаления/повтора */
  forceSelectableRows?: boolean;
  /** Своя кнопка создания вместо стандартной; отключает стандартный диалог */
  createComponent?: () => React.ReactNode;
  /** Не рисовать кнопку создания вовсе */
  hideCreateButton?: boolean;
  /** Своя кнопка удаления вместо стандартной (только для карточек) */
  deleteComponent?: (
    selectedIds: string[],
    onClearSelection?: () => void
  ) => React.ReactNode;
  /** Полностью заменяет набор кнопок над выбранными строками */
  selectableComponent?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
}

export function CRUD<
  Row extends { id: string } = { id: string },
  Filter = Record<string, unknown>,
>({
  totalSchema,

  filterSchema,
  filterQuery,
  filterQueryVariables,
  autoFilter = true,

  listQuery,
  listPollInterval,
  listQueryVariables,
  listMetrics,

  texts,

  getTableData,
  mapColumns,
  columns,

  download,
  downloadReportType,
  downloadButton,

  normalizeFilter,

  createSchema,

  deleteMutation,
  deleteMutationVariables,

  repeatMutation,
  repeatMutationVariables,

  repeatFromHlsMutation,

  restoreMutation,

  editSchema,
  // editQuery,

  children,
  createInitialVariables,
  onSubmitEditDialog,
  persistFilters,
  headButtons,
  useMobileView,
  mobileColumns,
  hasShowSelect,
  forceSelectableRows,
  createComponent,
  hideCreateButton,
  selectableComponent,
  deleteComponent,
}: CRUDProps<Row, Filter>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab");
  const id = searchParams.get("id");

  const onSubmitCreateDialog = ({ data }: { data: any }) => {
    if (data.created) {
      searchParams.set("id", data.created.id);
      searchParams.set("tab", "main");
    } else {
      searchParams.delete("tab");
      searchParams.delete("id");
    }
    setSearchParams(searchParams, { replace: true });
    enqueueSnackbar(texts?.itemCreated, { variant: "success" });
  };

  const onCloseCreateDialog = (): void => {
    searchParams.delete("tab");
    setSearchParams(searchParams, { replace: true });
  };

  const onCloseEditDialog = (): void => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams, { replace: true });
  };

  const onClickTab = (tab: string): void => {
    searchParams.set("tab", tab);
    setSearchParams(searchParams, { replace: true });
  };

  const listQueryName: string =
    listQuery.definitions.find(
      (definition): definition is OperationDefinitionNode =>
        definition.kind === "OperationDefinition" &&
        definition.operation === "query"
    )?.name?.value || "unknown";

  const refetchQueries: string[] = [listQueryName];

  return (
    <>
      <Routes>
        <Route element={<CrudLayout />}>
          <Route
            index
            element={
              <CrudList<Row, Filter>
                listMetrics={listMetrics}
                totalSchema={totalSchema}
                filterSchema={filterSchema}
                filterQuery={filterQuery}
                filterQueryVariables={filterQueryVariables}
                autoFilter={autoFilter}
                listQuery={listQuery}
                listPollInterval={listPollInterval}
                listQueryVariables={listQueryVariables}
                texts={defaultTexts(t, texts)}
                getTableData={getTableData}
                mapColumns={mapColumns}
                columns={columns}
                download={download}
                downloadReportType={downloadReportType}
                normalizeFilter={normalizeFilter}
                createSchema={createSchema}
                deleteMutation={deleteMutation}
                deleteMutationVariables={deleteMutationVariables}
                repeatMutation={repeatMutation}
                repeatMutationVariables={repeatMutationVariables}
                repeatFromHlsMutation={repeatFromHlsMutation}
                restoreMutation={restoreMutation}
                downloadButton={downloadButton}
                persistFilters={persistFilters}
                useMobileView={useMobileView}
                mobileColumns={mobileColumns}
                hasShowSelect={hasShowSelect}
                forceSelectableRows={forceSelectableRows}
                headButtons={headButtons}
                selectableComponent={selectableComponent}
                customDeleteButton={deleteComponent}
                createButton={createComponent ? createComponent() : undefined}
                enableCreateButton={
                  !hideCreateButton && !createComponent && !!createSchema
                }
              />
            }
          />
          {children}
        </Route>
      </Routes>

      {!createComponent && createSchema && tab && (
        <CrudCreateDialog
          open={tab === "create"}
          onClose={onCloseCreateDialog}
          texts={defaultTexts(t, texts) as { createItem: string }}
          createSchema={createSchema}
          onSubmitSuccess={onSubmitCreateDialog}
          refetchQueries={refetchQueries}
          initialVariables={createInitialVariables}
        />
      )}

      {editSchema && tab && id && (
        <CrudEditDialog
          open={!!id}
          id={id}
          tab={tab}
          onClickTab={onClickTab}
          onClose={onCloseEditDialog}
          editSchema={editSchema}
          // editQuery={editQuery}
          texts={defaultTexts(t, texts) as { editItem: string }}
          refetchQueries={refetchQueries}
          onSubmitSuccess={onSubmitEditDialog}
        />
      )}
    </>
  );
}
